import React, { useState, useEffect, useCallback } from 'react';
import { chequeService, extractErrorDetails } from '../services/api';
import ErrorDisplay from './ErrorDisplay';

const emptyFormData = {
  numero: '',
  fechaEmision: '',
  fechaVencimiento: '',
  importe: ''
};

const emptyFilters = {
  numero: '',
  fromFechaEmision: '',
  toFechaEmision: '',
  fromFechaVencimiento: '',
  toFechaVencimiento: ''
};

function ChequeManagement({ initialEditCheque, onEditHandled }) {
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(!!initialEditCheque);
  const [editingCheque, setEditingCheque] = useState(initialEditCheque || null);
  const [formData, setFormData] = useState(
    initialEditCheque ? chequeToFormData(initialEditCheque) : emptyFormData
  );

  const [filterInputs, setFilterInputs] = useState(emptyFilters);
  const [filters, setFilters] = useState(emptyFilters);
  const [pagination, setPagination] = useState({ page: 0, totalPages: 0, totalElements: 0 });

  function chequeToFormData(cheque) {
    return {
      numero: cheque.numero,
      fechaEmision: cheque.fechaEmision,
      fechaVencimiento: cheque.fechaVencimiento,
      importe: cheque.importe
    };
  }

  const fetchCheques = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = { ...filters, page: pagination.page, size: 20 };
      Object.keys(params).forEach(key => {
        if (params[key] === '') delete params[key];
      });

      const response = await chequeService.getCheques(params);
      if (response.success) {
        const data = response.data;
        setCheques(data.content || []);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages,
          totalElements: data.totalElements
        }));
      } else {
        setError(response.message || 'Error al cargar los cheques');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al cargar los cheques');
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page]);

  useEffect(() => {
    fetchCheques();
    if (initialEditCheque && onEditHandled) {
      onEditHandled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCheques]);

  const handleFilterInputChange = (field, value) => {
    setFilterInputs(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setFilters(filterInputs);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handleClearFilters = () => {
    setFilterInputs(emptyFilters);
    setFilters(emptyFilters);
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const payload = {
      ...formData,
      importe: parseFloat(formData.importe)
    };

    try {
      let response;
      if (editingCheque) {
        response = await chequeService.updateCheque(editingCheque.id, payload);
      } else {
        response = await chequeService.createCheque(payload);
      }

      if (response.success) {
        setSuccess(editingCheque ? 'Cheque actualizado exitosamente' : 'Cheque creado exitosamente');
        resetForm();
        fetchCheques();
      } else {
        setError(response.message || 'Error al guardar el cheque');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al guardar el cheque');
      setError(message);
    }
  };

  const handleEdit = (cheque) => {
    setEditingCheque(cheque);
    setFormData(chequeToFormData(cheque));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este cheque?')) {
      try {
        setError('');
        const response = await chequeService.deleteCheque(id);
        if (response.success) {
          setSuccess('Cheque eliminado exitosamente');
          fetchCheques();
        } else {
          setError(response.message || 'Error al eliminar el cheque');
        }
      } catch (err) {
        const { message } = extractErrorDetails(err, 'Error al eliminar el cheque');
        setError(message);
      }
    }
  };

  const resetForm = () => {
    setFormData(emptyFormData);
    setEditingCheque(null);
    setShowForm(false);
  };

  const openNewChequeForm = () => {
    resetForm();
    setShowForm(true);
  };

  const formatImporte = (importe) => {
    return new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }).format(importe);
  };

  return (
    <div className="cheque-management">
      <div className="section-header">
        <h2>Gestión de Cheques</h2>
        <div className="header-actions">
          <button onClick={openNewChequeForm} className="btn btn-primary">
            + Nuevo Cheque
          </button>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <label htmlFor="filtroNumero">Número</label>
          <input
            type="text"
            id="filtroNumero"
            value={filterInputs.numero}
            onChange={(e) => handleFilterInputChange('numero', e.target.value)}
            placeholder="Ej: 1001"
          />
        </div>

        <div className="filter-group">
          <label htmlFor="fromFechaEmision">Emisión desde</label>
          <input
            type="date"
            id="fromFechaEmision"
            value={filterInputs.fromFechaEmision}
            onChange={(e) => handleFilterInputChange('fromFechaEmision', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="toFechaEmision">Emisión hasta</label>
          <input
            type="date"
            id="toFechaEmision"
            value={filterInputs.toFechaEmision}
            onChange={(e) => handleFilterInputChange('toFechaEmision', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="fromFechaVencimiento">Vencimiento desde</label>
          <input
            type="date"
            id="fromFechaVencimiento"
            value={filterInputs.fromFechaVencimiento}
            onChange={(e) => handleFilterInputChange('fromFechaVencimiento', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="toFechaVencimiento">Vencimiento hasta</label>
          <input
            type="date"
            id="toFechaVencimiento"
            value={filterInputs.toFechaVencimiento}
            onChange={(e) => handleFilterInputChange('toFechaVencimiento', e.target.value)}
          />
        </div>
      </div>

      <div className="filter-actions">
        <button onClick={handleApplyFilters} className="btn btn-primary">
          Aplicar Filtros
        </button>
        <button onClick={handleClearFilters} className="btn btn-secondary">
          Limpiar
        </button>
      </div>

      <ErrorDisplay error={error} />
      {success && <p className="success-message">{success}</p>}

      {showForm && (
        <div className="cheque-form-container">
          <form onSubmit={handleSubmit} className="cheque-form">
            <h3>{editingCheque ? `Editar Cheque #${editingCheque.id}` : 'Nuevo Cheque'}</h3>

            <div className="input-group">
              <label htmlFor="numero">Número</label>
              <input
                type="text"
                id="numero"
                name="numero"
                value={formData.numero}
                onChange={handleChange}
                placeholder="Número de cheque"
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="fechaEmision">Fecha de Emisión</label>
              <input
                type="date"
                id="fechaEmision"
                name="fechaEmision"
                value={formData.fechaEmision}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="fechaVencimiento">Fecha de Vencimiento</label>
              <input
                type="date"
                id="fechaVencimiento"
                name="fechaVencimiento"
                value={formData.fechaVencimiento}
                onChange={handleChange}
                required
              />
            </div>

            <div className="input-group">
              <label htmlFor="importe">Importe ($)</label>
              <input
                type="number"
                id="importe"
                name="importe"
                value={formData.importe}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
                min="0.01"
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingCheque ? 'Actualizar' : 'Crear'}
              </button>
              <button type="button" onClick={resetForm} className="btn btn-secondary">
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p>Cargando cheques...</p>
      ) : cheques.length === 0 ? (
        <div className="empty-state">
          <p>No hay cheques que coincidan con los filtros</p>
        </div>
      ) : (
        <>
          <div className="cheques-table">
            <table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Fecha Emisión</th>
                  <th>Fecha Vencimiento</th>
                  <th>Importe</th>
                  <th>Cargado por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cheques.map((cheque) => (
                  <tr key={cheque.id}>
                    <td>{cheque.numero}</td>
                    <td>{cheque.fechaEmision}</td>
                    <td>{cheque.fechaVencimiento}</td>
                    <td>{formatImporte(cheque.importe)}</td>
                    <td>{cheque.createdByNombre}</td>
                    <td className="actions-cell">
                      <button
                        onClick={() => handleEdit(cheque)}
                        className="btn btn-small"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(cheque.id)}
                        className="btn btn-small btn-danger"
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 0}
                className="btn btn-small"
              >
                Anterior
              </button>
              <span>Página {pagination.page + 1} de {pagination.totalPages} ({pagination.totalElements} cheques)</span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages - 1}
                className="btn btn-small"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChequeManagement;
